/*
  Lunqet: behaviour for the book page (sections/lq-pdp.liquid and sections/lq-chapters.liquid).
  - Edition tiles switch cover, button labels and the sticky bar in place.
  - Every button with form="lq-buy-form" adds the chosen edition through Horizon's cart events,
    so the cart drawer and count update the native way, then the drawer opens.
  - End-of-page offer: once per session, with a real per-visitor deadline that never restarts.
  - The cart drawer offers live in snippets/lq-cart-offers.liquid and assets/lq-cart.js.
  - Recent orders: shows only the orders the merchant entered, each once per session.
*/
import { CartLinesUpdateEvent } from '@shopify/events';

const root = document.querySelector('[data-lq-pdp]');
if (root && !window.__lqPdp) {
  window.__lqPdp = true;
  init();
}

/* Horizon declares Theme with const, so it is a global binding but not a property of window. */
const routes = (typeof Theme !== 'undefined' && Theme.routes) || {
  cart_add_url: '/cart/add.js',
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  cart_url: '/cart',
};

function init() {
  const form = document.getElementById('lq-buy-form');
  const money = (cents) => '$' + (cents / 100).toFixed(2);
  const store = {
    get(k, session) { try { return (session ? sessionStorage : localStorage).getItem(k); } catch (e) { return null; } },
    set(k, v, session) { try { (session ? sessionStorage : localStorage).setItem(k, v); } catch (e) {} },
  };
  const tellId = root.dataset.tell;

  /* ---------- Cart ---------- */
  async function addLines(items, source) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    (source || document).dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));
    try {
      const res = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'add');
      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lq-pdp', itemCount: items.length, sections: json.sections, didError: false },
      });
      return cart;
    } catch (error) {
      deferred.resolve({ detail: { didError: true } });
      throw error;
    }
  }
  function getCart() {
    return fetch(routes.cart_url + '.js', { cache: 'no-store' }).then((r) => r.json());
  }
  /* Adds a discount code to the cart and lets Horizon re-render the drawer with the new total. */
  async function applyDiscount(code) {
    const cart = await getCart();
    const first = (cart.items || [])[0];
    if (!first) return;
    const codes = [...(cart.discount_codes || []).map((d) => d.code).filter((c) => c && c !== code), code];
    const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean).join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'product', lines: [{ id: first.key, quantity: first.quantity }], promise: deferred.promise }));
    try {
      const res = await fetch(routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections }),
      }).then((r) => r.json());
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(res), detail: { items: res.items, sections: res.sections, source: 'lq-pdp', didError: false } });
    } catch (e) {
      deferred.resolve({ detail: { didError: true } });
    }
  }
  function openDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  }

  /* ---------- Editions ---------- */
  const covers = root.querySelectorAll('[data-lq-cover]');
  const sticky = document.querySelector('[data-lq-sticky]');
  const stickyMeta = sticky && sticky.querySelector('[data-lq-sticky-meta]');
  const stickyImg = sticky && sticky.querySelector('[data-lq-sticky-img]');
  const chosen = () => form.querySelector('input[name="id"]:checked');

  function applyEdition() {
    const r = chosen();
    if (!r) return;
    covers.forEach((c) => c.classList.toggle('is-on', c.dataset.lqCover === r.dataset.lqEd));
    document.querySelectorAll('[data-lq-label]').forEach((el) => { el.textContent = r.dataset.cta; });
    if (stickyMeta) stickyMeta.textContent = r.dataset.name + ', ' + r.dataset.price;
    if (stickyImg && r.dataset.img) stickyImg.src = r.dataset.img;
    const offerEl = document.querySelector('[data-lq-offer]');
    if (offerEl && offerEl.dataset.edition === '0') {
      const offerRadio = offerEl.querySelector('input[data-lq-ed="' + r.dataset.lqEd + '"]');
      if (offerRadio) offerRadio.checked = true;
    }
  }
  form.addEventListener('change', applyEdition);
  applyEdition();

  /* ---------- Strings: tie each one to the top edge of the book in its photo ---------- */
  function rig(cover) {
    const strings = cover.querySelector('.lq-pdp__strings');
    if (!strings || !cover.dataset.probe) return;
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      try {
        const w = 120;
        const h = Math.round((w * probe.naturalHeight) / probe.naturalWidth);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        const top = [];
        for (let x = 0; x < w; x++) {
          top[x] = null;
          for (let y = 0; y < h; y++) {
            if (data[(y * w + x) * 4 + 3] > 200) { top[x] = y; break; }
          }
        }
        const found = top.filter((y) => y !== null);
        if (!found.length) return;
        const highest = Math.min(...found);
        /* the book: columns whose top edge sits near the highest point (leaves out a phone standing lower) */
        const cols = top.map((y, x) => (y !== null && y <= highest + h * 0.08 ? x : null)).filter((x) => x !== null);
        const left = cols[0];
        const right = cols[cols.length - 1];
        strings.querySelectorAll('i').forEach((el, i) => {
          const x = Math.round(left + (right - left) * [0.2, 0.5, 0.8][i]);
          let y = top[x];
          if (y === null) y = highest;
          el.style.setProperty('--x', ((x + 0.5) / w) * 100 + '%');
          el.style.setProperty('--y', ((y + 1) / h) * 100 + '%');
        });
        strings.classList.add('is-set');
      } catch (e) {
        /* without the measurement the strings simply stay hidden */
      }
    };
    probe.src = cover.dataset.probe;
  }
  covers.forEach(rig);

  let offerOnSubmit = null;

  /* ---------- Every button on the page takes the chosen edition ---------- */
  const err = form.querySelector('[data-lq-err]');
  const status = form.querySelector('[data-lq-status]');
  form.addEventListener('submit', async (event) => {
    const r = chosen();
    if (!r) return;
    event.preventDefault();
    /* The offer can be set to open the first time the main button is pressed, once per visit. */
    if (offerOnSubmit && offerOnSubmit()) return;
    const button = event.submitter || form.querySelector('[data-lq-go]');
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    err.textContent = '';
    try {
      const lines = [{ id: Number(r.value), quantity: 1 }];
      form.querySelectorAll('[data-lq-bump]').forEach((bump) => {
        if (bump.checked) lines.push({ id: Number(bump.value), quantity: 1 });
      });
      await addLines(lines, form);
      status.textContent = r.dataset.name + ' is in your cart.';
      openDrawer();
    } catch (e) {
      err.textContent = 'That didn’t go through. Please try again.';
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Sticky bar: after the main button has scrolled away ---------- */
  const mainButton = form.querySelector('[data-lq-go]');
  if (sticky && mainButton && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-on', show);
      sticky.inert = !show;
      document.body.classList.toggle('lq-sticky-on', show);
    }).observe(mainButton);
  }

  /* ---------- Decoder ---------- */
  const lines = document.querySelectorAll('[data-lq-q]');
  lines.forEach((b) => {
    b.addEventListener('click', () => b.setAttribute('aria-expanded', b.getAttribute('aria-expanded') === 'true' ? 'false' : 'true'));
  });
  /* Touch screens have no hover: each Method reveals itself as its line reaches the middle of the screen. */
  if (window.matchMedia('(hover: none)').matches && 'IntersectionObserver' in window) {
    const reveal = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('aria-expanded', 'true');
        reveal.unobserve(entry.target);
      });
    }, { rootMargin: '-40% 0px -40% 0px' });
    lines.forEach((b) => reveal.observe(b));
  }

  /* ---------- Review screenshots open full size ---------- */
  document.querySelectorAll('[data-lq-shot]').forEach((dialog) => {
    const img = dialog.querySelector('[data-lq-shot-img]');
    const section = dialog.closest('section') || document;
    section.querySelectorAll('[data-lq-shot-open]').forEach((b) => {
      b.addEventListener('click', () => {
        img.src = b.dataset.src;
        img.alt = b.dataset.alt || '';
        dialog.showModal();
      });
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-lq-shot-close]')) dialog.close();
    });
  });

  /* ---------- A deadline that starts once per visitor and never restarts ---------- */
  function deadline(key, ms) {
    let end = parseInt(store.get(key), 10);
    if (!end) { end = Date.now() + ms; store.set(key, String(end)); }
    return end;
  }
  const ended = (key) => { const v = parseInt(store.get(key), 10); return !!v && Date.now() > v; };
  const clock = (ms, hours) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    const p = (n) => String(n).padStart(2, '0');
    return hours
      ? p(Math.floor(s / 3600)) + ':' + p(Math.floor((s % 3600) / 60)) + ':' + p(s % 60)
      : p(Math.floor(s / 60)) + ':' + p(s % 60);
  };

  /* ---------- End-of-page offer ---------- */
  const offer = document.querySelector('[data-lq-offer]');
  const offerKey = (offer && offer.dataset.key) || 'book';
  const OFFER_END = 'lq_offer_end_' + offerKey;
  const OFFER_SEEN = 'lq_offer_seen_' + offerKey;
  if (offer && typeof offer.showModal === 'function') {
    const pct = parseFloat(offer.dataset.pct) || 0;
    const code = offer.dataset.code;
    const tellBox = offer.querySelector('[data-lq-offer-tell]');
    const tellCents = parseInt(root.dataset.tellCents, 10) || 0;
    /* Companions offered with the code (all start ticked). The code needs at least one of them in the order. */
    const adds = [...offer.querySelectorAll('[data-lq-offer-add]')];
    const addIds = adds.map((a) => a.value);
    const required = offer.dataset.require === 'true' && adds.length > 0;
    const tellOn = () => !!tellBox && tellBox.checked;
    const needEl = offer.querySelector('[data-lq-offer-need]');
    const go = offer.querySelector('[data-lq-offer-go]');
    const goLabel = go.textContent;
    const totalLabel = offer.querySelector('.lq-offer__total > span');
    const totalText = totalLabel ? totalLabel.textContent : '';
    const offerErr = offer.querySelector('[data-lq-offer-err]');
    const clockEl = offer.querySelector('[data-lq-clock]');
    let timer = null;
    let expired = false;

    const skip = offer.querySelector('[data-lq-offer-skip]');
    let openedByCta = false;
    let cartLines = [];
    /* The total covers the whole order the visitor will see at checkout: what is already in the cart,
       the edition and companion picked here, and every add-on still ticked on the page. */
    const pageBumps = () => [...form.querySelectorAll('[data-lq-bump]')].filter((b) => b.checked && b !== tellBox);
    const addsOn = () => adds.filter((a) => a.checked);
    /* A companion counts whether it is ticked here, ticked on the page, or already in the cart. */
    const qualifies = (lines = cartLines) => !required
      || addsOn().length > 0
      || pageBumps().some((b) => addIds.includes(b.value))
      || lines.some((i) => addIds.includes(String(i.variant_id)));
    const total = () => {
      const r = offer.querySelector('input[type="radio"]:checked');
      const seen = new Set(cartLines.map((i) => String(i.variant_id)));
      let c = cartLines.reduce((sum, i) => sum + i.final_line_price, 0);
      /* What the code discounts: the editions and the companions, wherever they come from. */
      const entitled = new Set([...offer.querySelectorAll('input[name="lq-offer-ed"]')].map((x) => x.value).concat(addIds));
      /* Shopify rounds the percentage down on each line, so the discount here is summed line by line. */
      const lineOff = (cents) => Math.floor((cents * pct) / 100);
      let e = cartLines.filter((i) => entitled.has(String(i.variant_id))).reduce((sum, i) => sum + lineOff(i.final_line_price), 0);
      const add = (id, cents) => { if (id && !seen.has(String(id))) { seen.add(String(id)); c += cents; if (entitled.has(String(id))) e += lineOff(cents); } };
      if (r) add(r.value, parseInt(r.dataset.cents, 10) || 0);
      if (tellOn()) add(tellId, tellCents);
      addsOn().forEach((a) => add(a.value, parseInt(a.dataset.cents, 10) || 0));
      pageBumps().forEach((b) => add(b.value, parseInt(b.dataset.cents, 10) || 0));
      /* With companions required, the code takes the percentage off the book and its companions, only with one kept. */
      const ok = qualifies();
      const off = !expired && ok;
      const cut = required ? e : Math.floor((c * pct) / 100);
      offer.querySelector('[data-lq-offer-was]').textContent = off ? money(c) : '';
      /* Shopify rounds a percentage discount down to the cent, so the total here does the same. */
      offer.querySelector('[data-lq-offer-total]').textContent = money(c - (off ? cut : 0));
      if (needEl) needEl.hidden = ok;
      if (totalLabel) totalLabel.textContent = off ? totalText : (totalLabel.dataset.plain || 'Your total');
      offer.classList.toggle('is-unqualified', !ok);
      if (!expired) go.textContent = ok ? goLabel : (go.dataset.labelNone || goLabel);
    };
    const refreshCart = () => getCart().then((cart) => { cartLines = cart.items || []; total(); }).catch(() => {});
    const expire = () => {
      expired = true;
      clearInterval(timer);
      offer.classList.add('is-ended');
      if (clockEl) clockEl.textContent = '00:00';
      go.textContent = go.dataset.labelEnded;
      total();
    };
    const tick = (end) => {
      const left = end - Date.now();
      if (left <= 0) { expire(); return; }
      if (clockEl) clockEl.textContent = clock(left);
    };
    const gateOpen = () => document.documentElement.classList.contains('lq-gated');
    const canOpen = () => !offer.open && !store.get(OFFER_SEEN, true) && !ended(OFFER_END) && !gateOpen();
    const open = () => {
      if (!canOpen()) return false;
      const drawer = document.getElementById('cart-drawer');
      if (drawer && drawer.isOpen) return false;
      store.set(OFFER_SEEN, '1', true);
      const end = deadline(OFFER_END, (parseFloat(offer.dataset.minutes) || 4) * 60000);
      applyEdition();
      tick(end);
      if (!expired) timer = setInterval(() => tick(end), 1000);
      total();
      refreshCart();
      if (skip) skip.textContent = openedByCta ? skip.dataset.labelCta : skip.dataset.label;
      offer.showModal();
      document.dispatchEvent(new CustomEvent('lq:offer', { detail: { open: true } }));
      return true;
    };
    const close = () => { if (offer.open) offer.close(); };
    offer.addEventListener('close', () => {
      clearInterval(timer);
      document.dispatchEvent(new CustomEvent('lq:offer', { detail: { open: false } }));
    });
    /* The same companion ticked on the page follows the popup, so unticking it here really leaves it out. */
    offer.addEventListener('change', (e) => {
      const a = e.target.closest && e.target.closest('[data-lq-offer-add]');
      if (a) form.querySelectorAll('[data-lq-bump]').forEach((b) => {
        /* Set quietly: a change event here would make the page push its own edition back into the popup. */
        if (b.value === a.value) b.checked = a.checked;
      });
      total();
    });
    offer.addEventListener('click', async (e) => {
      /* Opened by the main button: the decline link still carries the page selection through to checkout. */
      if (openedByCta && e.target.closest('[data-lq-offer-skip]')) {
        const pick = chosen();
        if (!pick || skip.getAttribute('aria-busy') === 'true') return;
        skip.setAttribute('aria-busy', 'true');
        offerErr.textContent = '';
        try {
          const cart = await getCart();
          const have = new Set((cart.items || []).map((i) => String(i.variant_id)));
          const lines = [];
          if (!have.has(pick.value)) lines.push({ id: Number(pick.value), quantity: 1 });
          form.querySelectorAll('[data-lq-bump]').forEach((b) => {
            if (b.checked && !have.has(b.value)) lines.push({ id: Number(b.value), quantity: 1 });
          });
          if (lines.length) await addLines(lines, form);
          window.location.href = '/checkout';
        } catch (err2) {
          offerErr.textContent = 'That didn’t go through. Please try again.';
          skip.removeAttribute('aria-busy');
        }
        return;
      }
      if (e.target === offer || e.target.closest('[data-lq-offer-close]')) close();
    });
    go.addEventListener('click', async () => {
      const r = offer.querySelector('input[type="radio"]:checked');
      if (!r) return;
      go.disabled = true;
      offerErr.textContent = '';
      try {
        const cart = await getCart();
        const have = new Set((cart.items || []).map((i) => String(i.variant_id)));
        const items = [];
        if (!have.has(r.value)) items.push({ id: Number(r.value), quantity: 1 });
        if (tellOn() && tellId && !have.has(tellId)) items.push({ id: Number(tellId), quantity: 1 });
        addsOn().forEach((a) => { if (!have.has(a.value) && !items.some((i) => String(i.id) === a.value)) items.push({ id: Number(a.value), quantity: 1 }); });
        pageBumps().forEach((b) => { if (!have.has(b.value) && !items.some((i) => String(i.id) === b.value)) items.push({ id: Number(b.value), quantity: 1 }); });
        if (items.length) await addLines(items, offer);
        const usable = !expired && !ended(OFFER_END) && qualifies(cart.items || []);
        if (offer.dataset.after === 'cart') {
          if (usable) await applyDiscount(code);
          close();
          openDrawer();
          go.disabled = false;
          return;
        }
        window.location.href = usable ? '/checkout?discount=' + encodeURIComponent(code) : '/checkout';
      } catch (e) {
        offerErr.textContent = 'That didn’t go through. Please try again.';
        go.disabled = false;
      }
    });
    const finale = document.querySelector('[data-lq-final]');
    if (offer.dataset.cta === 'true') {
      /* Opened by the main button: keep the edition the visitor just chose. */
      offerOnSubmit = () => {
        if (!canOpen()) return false;
        const pick = chosen();
        const radio = pick && offer.querySelector('input[data-lq-ed="' + pick.dataset.lqEd + '"]');
        if (radio) radio.checked = true;
        openedByCta = true;
        if (open()) return true;
        openedByCta = false;
        return false;
      };
    }
    if (offer.dataset.exit === 'true' && window.matchMedia('(pointer: fine)').matches) {
      /* Exit intent: the pointer leaves the page through the top edge, toward the tabs and the address bar. */
      document.addEventListener('mouseout', (e) => {
        if (!e.relatedTarget && e.clientY <= 0) open();
      });
    }
    if (offer.dataset.bottom === 'true' && 'IntersectionObserver' in window) {
      const bottom = document.querySelector('.lq-footer') || document.querySelector('footer');
      if (bottom) {
        const bio = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting) && open()) bio.disconnect();
        }, { threshold: 0.2 });
        bio.observe(bottom);
      }
    }
    if (offer.dataset.trigger === 'time') {
      /* The delay is counted from the moment the entry gate is passed, when the page has one. */
      const wait = (parseInt(offer.dataset.seconds, 10) || 30) * 1000;
      const passedAt = parseInt(store.get('lq_gate_passed_at', true), 10);
      if (gateOpen()) {
        document.addEventListener('lq:gate-passed', () => setTimeout(open, wait), { once: true });
      } else if (document.querySelector('[data-lq-gate]') && passedAt) {
        /* Gate passed earlier in this visit: the delay still counts from that click. */
        setTimeout(open, Math.max(0, wait - (Date.now() - passedAt)));
      } else {
        setTimeout(open, wait);
      }
    } else if (finale && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { open(); io.disconnect(); }
      }, { threshold: 0.35 });
      io.observe(finale);
    }
  }

  /* ---------- Recent orders: the merchant's real orders only ---------- */
  const toast = document.querySelector('[data-lq-toast]');
  const TOAST_OFF = 'lq_orders_off';
  if (toast && !store.get(TOAST_OFF, true)) {
    const decode = (s) => { const t = document.createElement('textarea'); t.innerHTML = s; return t.value; };
    const fmt = decode(toast.querySelector('template[data-lq-order-format]').innerHTML.trim());
    const days = parseInt(toast.dataset.days, 10) || 14;
    const now = Date.now();
    const rtf = window.Intl && Intl.RelativeTimeFormat ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' }) : null;
    const orders = [...toast.querySelector('template[data-lq-orders]').content.querySelectorAll('i')]
      .map((i) => ({ ...i.dataset, time: Date.parse(i.dataset.date + 'T12:00:00') }))
      .filter((o) => o.name && o.time && !Number.isNaN(o.time) && o.time <= now && now - o.time <= days * 86400000);
    for (let i = orders.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [orders[i], orders[j]] = [orders[j], orders[i]];
    }
    const line = toast.querySelector('[data-lq-toast-line]');
    const when = toast.querySelector('[data-lq-toast-when]');
    const img = toast.querySelector('[data-lq-toast-img]');
    const gap = (parseInt(toast.dataset.gap, 10) || 30) * 1000;
    let index = 0;
    let paused = false;
    let hideTimer = null;
    let nextTimer = null;

    const ago = (t) => {
      const d = Math.max(0, Math.floor((now - t) / 86400000));
      return rtf ? rtf.format(-d, 'day') : d + ' days ago';
    };
    const hide = () => {
      toast.classList.remove('is-on');
      setTimeout(() => { if (!toast.classList.contains('is-on')) toast.hidden = true; }, 500);
    };
    const showNext = () => {
      if (index >= orders.length) return;
      if (paused) { nextTimer = setTimeout(showNext, 4000); return; }
      const o = orders[index++];
      let text = fmt.replace('[name]', o.name).replace('[edition]', o.ed || '');
      text = o.place ? text.replace('[place]', o.place) : text.replace(' in [place]', '').replace('[place]', '');
      line.textContent = text;
      when.textContent = ago(o.time);
      if (o.img) img.src = o.img;
      toast.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-on')));
      hideTimer = setTimeout(hide, 6500);
      nextTimer = setTimeout(showNext, gap);
    };
    toast.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    toast.addEventListener('mouseleave', () => { hideTimer = setTimeout(hide, 2500); });
    toast.querySelector('[data-lq-toast-close]').addEventListener('click', () => {
      store.set(TOAST_OFF, '1', true);
      clearTimeout(nextTimer);
      hide();
    });
    document.addEventListener('lq:offer', (e) => { paused = e.detail.open; if (paused) hide(); });
    if (orders.length) nextTimer = setTimeout(showNext, (parseInt(toast.dataset.delay, 10) || 10) * 1000);
  }
}
